import { errorMessagesIntl } from "./errorMessages.intl";

import { GLOBAL_ERROR_MESSAGE } from "@/app/constants/global-constants";
import { createTranslator } from "@/app/utils/intlMessage.utils";

const t = createTranslator(errorMessagesIntl);

const TECHNICAL_MESSAGE_PATTERNS = [
  /prisma/i,
  /postgres/i,
  /mysql/i,
  /sqlite/i,
  /sql/i,
  /foreign key/i,
  /unique constraint/i,
  /constraint failed/i,
  /syntaxerror/i,
  /exception/i,
  /stack trace/i,
  /at\s+.+:\d+/i,
];

const MAX_PUBLIC_MESSAGE_LENGTH = 220;

const STATUS_MESSAGES: Record<number, string> = {
  400: t("badRequest"),
  401: t("sessionExpired"),
  403: t("permissionDenied"),
  404: t("notFound"),
  409: t("conflict"),
  413: t("fileTooLarge"),
  429: t("tooManyRequests"),
  503: t("databaseUnavailable"),
  504: t("requestTimeout"),
};

const STRING_STATUS_MESSAGES: Record<string, string> = {
  FETCH_ERROR: t("fetchError"),
  PARSING_ERROR: GLOBAL_ERROR_MESSAGE,
  TIMEOUT_ERROR: t("requestTimeout"),
  CUSTOM_ERROR: GLOBAL_ERROR_MESSAGE,
};

// ---------------------------------------------------------------------------
// Prisma code → user-facing message map
// Built once at module init; O(1) lookup replaces the previous if-chain.
// ---------------------------------------------------------------------------
const PRISMA_CODE_MESSAGES: ReadonlyMap<string, string> = new Map<
  string,
  string
>([
  // Unique-constraint violations
  ["P2002", t("recordAlreadyExists")],

  // Foreign-key / relation violations (record in use)
  ...["P2003", "P2014", "P2017", "P2018", "P2027", "P2034"].map(
    (c): [string, string] => [c, t("recordInUse")],
  ),

  // Record not found
  ...["P2001", "P2015", "P2025"].map((c): [string, string] => [
    c,
    t("recordNotFound"),
  ]),

  // Timeout
  ...["P2024", "P6004"].map((c): [string, string] => [
    c,
    t("databaseTimedOut"),
  ]),

  // Invalid / malformed input — P2000 gets a more specific message
  ["P2000", t("valueTooLong")],
  ...[
    "P2005",
    "P2006",
    "P2007",
    "P2008",
    "P2009",
    "P2011",
    "P2012",
    "P2013",
    "P2019",
    "P2020",
    "P2026",
    "P2029",
    "P2030",
    "P2033",
    "P6001",
    "P6005",
  ].map((c): [string, string] => [c, t("badRequest")]),

  // Database unavailable
  ...[
    "P1000",
    "P1001",
    "P1002",
    "P1003",
    "P1008",
    "P1010",
    "P1011",
    "P1015",
    "P1017",
    "P2004",
    "P2010",
    "P2016",
    "P2021",
    "P2022",
    "P2023",
    "P2028",
    "P2031",
    "P2035",
    "P2036",
    "P2037",
    "P2038",
    "P2039",
    "P2040",
    "P2041",
    "P2042",
    "P2043",
    "P2044",
    "P2045",
    "P2046",
    "P2047",
    "P2048",
    "P2049",
    "P2050",
    "P3000",
    "P3001",
    "P3002",
    "P3003",
    "P3004",
    "P3005",
    "P3006",
    "P3007",
    "P3008",
    "P3009",
    "P3010",
    "P3011",
    "P3012",
    "P3013",
    "P3014",
    "P3015",
    "P3016",
    "P3017",
    "P3018",
    "P3019",
    "P3020",
    "P3021",
    "P3022",
    "P3023",
    "P3024",
    "P4000",
    "P4001",
    "P4002",
    "P6000",
    "P6002",
    "P6003",
    "P6006",
    "P6008",
    "P6009",
    "P6010",
    "P5011",
  ].map((c): [string, string] => [c, t("databaseUnavailable")]),
]);

type ApiErrorLike = {
  status?: number | string;
  message?: unknown;
  code?: unknown;
  data?: {
    message?: unknown;
    meta?: {
      code?: unknown;
    };
    code?: unknown;
  };
};

const toText = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();

  return trimmed.length > 0 ? trimmed : undefined;
};

const isSafePublicMessage = (message: string): boolean => {
  if (message.length > MAX_PUBLIC_MESSAGE_LENGTH) {
    return false;
  }

  return !TECHNICAL_MESSAGE_PATTERNS.some((pattern) => pattern.test(message));
};

const getMessageForStatus = (status: number | string): string | undefined => {
  if (typeof status === "string") {
    return STRING_STATUS_MESSAGES[status];
  }

  return STATUS_MESSAGES[status];
};

const getPrismaMessageForCode = (code: string): string | undefined =>
  PRISMA_CODE_MESSAGES.get(code) ??
  (code.startsWith("P6") ? t("databaseUnavailable") : undefined);

const getPrismaCode = (error: ApiErrorLike): string | undefined => {
  return (
    toText(error.data?.meta?.code) ??
    toText(error.data?.code) ??
    toText(error.code)
  );
};

export const resolveUserFacingErrorMessage = (
  error: unknown,
  fallback: string = GLOBAL_ERROR_MESSAGE,
): string => {
  if (!error || typeof error !== "object") {
    return fallback;
  }

  const apiError = error as ApiErrorLike;
  const status = apiError.status;
  const prismaCode = getPrismaCode(apiError);
  const responseMessage =
    toText(apiError.data?.message) ?? toText(apiError.message);

  if (prismaCode) {
    const prismaMessage = getPrismaMessageForCode(prismaCode);

    if (prismaMessage) {
      return prismaMessage;
    }
  }

  const statusMessage =
    typeof status === "number" || typeof status === "string"
      ? getMessageForStatus(status)
      : undefined;

  if (typeof status === "number" && status >= 500) {
    return statusMessage ?? fallback;
  }

  if (responseMessage && isSafePublicMessage(responseMessage)) {
    return responseMessage;
  }

  if (statusMessage) {
    return statusMessage;
  }

  return fallback;
};

export const isSafeErrorMessage = (message: string | undefined): boolean => {
  if (!message) {
    return false;
  }

  return isSafePublicMessage(message);
};
